/*
 * Gourmet Easy PDP thumbnail rail.
 *
 * Dawn stacks every thumbnail, so a product with twelve images gives a rail
 * roughly twice the height of the image it sits beside. This caps the rail
 * at seven slots -- the number that matches the square gallery -- with any
 * video pinned to the bottom and a "More" control taking the last image
 * slot when there are more images than fit.
 *
 * Desktop only: below 990px the rail is a horizontal slider, where none of
 * this applies. Everything is undone cleanly when the viewport crosses back
 * under that breakpoint, so a resize cannot leave items hidden.
 */
(() => {
  const SLOTS = 7;
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  function reset(rail) {
    rail.querySelectorAll('[data-ge-thumb-hidden], [data-ge-collapsible]').forEach((li) => {
      li.removeAttribute('data-ge-thumb-hidden');
      li.removeAttribute('data-ge-collapsible');
    });
    rail.querySelectorAll('[data-ge-more]').forEach((el) => el.remove());
    rail.querySelectorAll('[data-ge-order]').forEach((li) => {
      li.style.removeProperty('order');
      li.removeAttribute('data-ge-order');
    });
    rail.removeAttribute('data-ge-expanded');
    rail.removeAttribute('data-ge-sig');
  }

  function apply(gallery) {
    const rail = gallery.querySelector('.thumbnail-list');
    if (!rail) return;

    /*
     * Rebuild only when the thumbnails themselves have changed.
     *
     * Without this the rail was torn down and recreated on every mutation
     * inside the gallery -- including the ones a mousedown produces. The
     * More button was therefore detached between mousedown and click, so
     * by the time the click handler ran btn.closest('.thumbnail-list') was
     * null and it bailed. The control worked when clicked from script and
     * did nothing at all when clicked with a mouse.
     */
    const signature = [...rail.querySelectorAll('.thumbnail-list__item:not([data-ge-more])')]
      .map((li) => li.getAttribute('data-target') || '')
      .join(',');
    if (rail.getAttribute('data-ge-sig') === signature && rail.querySelector('[data-ge-more]')) {
      return;
    }

    reset(rail);
    if (!DESKTOP.matches) return;
    rail.setAttribute('data-ge-sig', signature);

    /*
     * Only thumbnails Dawn is actually showing. With hide_variants on it
     * hides the per-variant thumbs in CSS, and counting those made a
     * twelve-image product think it had far more slots to fill than the
     * rail was rendering -- Kitchen Scale ended up with four.
     */
    const items = [...rail.querySelectorAll('.thumbnail-list__item')].filter(
      (li) => getComputedStyle(li).display !== 'none'
    );
    if (!items.length) return;

    const isVideo = (li) => {
      const t = li.getAttribute('data-ge-media-type') || '';
      return t === 'video' || t === 'external_video' || t === 'model';
    };
    const videos = items.filter(isVideo);
    const images = items.filter((li) => !isVideo(li));

    /*
     * Video sinks to the bottom via flex order rather than by moving nodes:
     * Dawn's media-gallery keeps its own references to these elements for
     * keyboard navigation and active-state syncing, and reparenting them
     * breaks that.
     */
    images.forEach((li, i) => {
      li.style.order = String(i);
      li.setAttribute('data-ge-order', '');
    });
    videos.forEach((li, i) => {
      li.style.order = String(1000 + i);
      li.setAttribute('data-ge-order', '');
    });

    // Videos always get a slot each; the images share what is left.
    const imageSlots = SLOTS - videos.length;
    if (imageSlots < 2 || images.length <= imageSlots) return;

    // One slot goes to the More control, so the rest show images.
    const shown = imageSlots - 1;
    const hidden = images.slice(shown);
    /*
     * Two attributes, not one: -hidden is the current state and comes and
     * goes as the control is toggled, -collapsible is the permanent record
     * of which items the control owns. Without the second one there is
     * nothing to re-hide on collapse.
     */
    hidden.forEach((li) => {
      li.setAttribute('data-ge-thumb-hidden', '');
      li.setAttribute('data-ge-collapsible', '');
    });

    const more = document.createElement('li');
    more.className = 'thumbnail-list__item ge-thumb-more';
    more.setAttribute('data-ge-more', '');
    more.style.order = String(shown);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ge-thumb-more__btn';
    btn.setAttribute('aria-expanded', 'false');
    const count = document.createElement('span');
    count.className = 'ge-thumb-more__count';
    count.textContent = '+' + hidden.length;
    const word = document.createElement('span');
    word.className = 'ge-thumb-more__word';
    word.textContent = 'More';
    btn.append(count, word);
    more.appendChild(btn);

    rail.appendChild(more);
  }

  function applyAll() {
    document.querySelectorAll('.product__media-wrapper media-gallery').forEach(apply);
  }

  /*
   * Delegated, and reading state from the DOM rather than a closure. The
   * rail gets rebuilt whenever the gallery re-renders, so a listener bound
   * to a particular button stops matching the button on the page -- which
   * is exactly why the control appeared inert.
   */
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest && event.target.closest('.ge-thumb-more__btn');
      if (!btn) return;
      /* Capture phase, and the event is stopped here: Dawn's media-gallery
         binds its own handler to thumbnail clicks and would treat this
         control as a media item, swapping the main image to nothing. */
      event.preventDefault();
      event.stopPropagation();
      const rail = btn.closest('.thumbnail-list');
      if (!rail) return;

      const collapsible = [...rail.querySelectorAll('[data-ge-collapsible]')];
      const expanding = !rail.hasAttribute('data-ge-expanded');

      collapsible.forEach((li) => {
        if (expanding) li.removeAttribute('data-ge-thumb-hidden');
        else li.setAttribute('data-ge-thumb-hidden', '');
      });

      rail.toggleAttribute('data-ge-expanded', expanding);
      btn.setAttribute('aria-expanded', expanding ? 'true' : 'false');
      btn.querySelector('.ge-thumb-more__count').textContent = expanding
        ? '−'
        : '+' + collapsible.length;
      btn.querySelector('.ge-thumb-more__word').textContent = expanding ? 'Less' : 'More';
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAll);
  } else {
    applyAll();
  }

  /*
   * The gallery is re-rendered when a variant changes, which replaces the
   * thumbnail list and drops everything done above.
   *
   * The observer is disconnected for the duration of applyAll rather than
   * guarded by a flag: appending the More button is itself a childList
   * mutation, so the observer kept firing on its own writes and rebuilding
   * the button -- which threw away the expanded state on every click.
   */
  const watched = [...document.querySelectorAll('.product__media-wrapper')];
  const observer = new MutationObserver(() => {
    observer.disconnect();
    applyAll();
    watch();
  });
  function watch() {
    watched.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
  }
  watch();

  function applyAllWatched() {
    observer.disconnect();
    applyAll();
    watch();
  }
  DESKTOP.addEventListener('change', applyAllWatched);
})();
